import { useState, useEffect, useCallback, useMemo } from 'react';
import supabaseService from '../services/supabaseService';

/**
 * Custom hook for managing interview data and operations
 * Provides optimized data fetching, caching, and real-time updates
 */
export const useInterviews = (employerId, filters = {}) => {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState({
    scheduled: 0,
    completed: 0,
    expired: 0,
    cancelled: 0,
    total: 0
  });

  // Memoized filters to prevent unnecessary re-fetches
  const memoizedFilters = useMemo(() => filters, [
    filters
  ]);

  // Fetch interviews with error handling and caching
  const fetchInterviews = useCallback(async () => {
    if (!employerId) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Auto-expire any scheduled interviews that have passed their scheduled time
      const { expiredCount, error: expireError } = 
        await supabaseService.interviews.autoExpireInterviews(employerId);
      
      if (expireError) {
        console.warn('Warning: Could not auto-expire interviews:', expireError);
        // Don't throw - continue fetching as this is not critical
      }

      const { interviews: fetchedInterviews, error: interviewError } = 
        await supabaseService.interviews.getEmployerInterviews(employerId);

      if (interviewError) {
        throw new Error(interviewError);
      }

      // Transform and normalize the data
      const normalizedInterviews = (fetchedInterviews || []).map(interview => {
        // Safely handle scheduled_at date
        let scheduledAt;
        let scheduled_date = 'TBD';
        let scheduled_time = 'TBD';
        
        if (interview.scheduled_at) {
          scheduledAt = new Date(interview.scheduled_at);
          
          // Check if the date is valid
          if (!isNaN(scheduledAt.getTime())) {
            scheduled_date = scheduledAt.toISOString().split('T')[0];
            scheduled_time = scheduledAt.toTimeString().slice(0, 5);
          } else {
            console.warn('Invalid scheduled_at date for interview:', interview.id, interview.scheduled_at);
            scheduledAt = null;
          }
        } else {
          scheduledAt = null;
        }
        
        // Parse feedback data safely - feedback can be JSON object or plain text
        let feedbackData = {};
        let rating = null;
        if (interview.feedback) {
          try {
            // If feedback is already an object, use it directly
            if (typeof interview.feedback === 'object' && interview.feedback !== null) {
              feedbackData = interview.feedback;
            } else if (typeof interview.feedback === 'string') {
              // Try to parse as JSON, but handle gracefully if it fails
              feedbackData = JSON.parse(interview.feedback);
            } else {
              feedbackData = { feedback: String(interview.feedback) };
            }
            rating = feedbackData.rating || null;
          } catch (e) {
            // If JSON parsing fails, treat it as plain text feedback
            console.debug('Interview feedback is not valid JSON, treating as plain text:', {
              interviewId: interview.id,
              feedbackType: typeof interview.feedback,
              feedbackPreview: String(interview.feedback).substring(0, 50) + '...'
            });
            feedbackData = { 
              feedback: String(interview.feedback),
              rating: null 
            };
          }
        }

        return {
          id: interview.id,
          candidate_id: interview.candidate_id,
          candidate_name: interview.candidate_name || 'Unknown Candidate',
          candidate_email: interview.candidate_email || '',
          candidate_avatar: interview.candidate_avatar || null,
          job_title: interview.job_title || 'Unknown Position',
          job_id: interview.job_id,
          application_id: interview.application_id,
          interviewer_id: interview.interviewer_id,
          scheduled_date: scheduled_date,
          scheduled_time: scheduled_time,
          scheduled_at: scheduledAt,
          duration: interview.duration || 60, // Use the mapped duration from service
          type: interview.type || 'video', // Use the mapped type from service
          status: interview.status || 'scheduled',
          meeting_link: interview.meeting_link || '',
          phone_number: interview.phone_number || '',
          notes: interview.notes || '',
          feedback: feedbackData.feedback || '',
          rating: rating,
          hiring_status: interview.hiring_status || 'interviewed',
          hiring_notes: interview.hiring_notes || '',
          considered_for_hire: interview.considered_for_hire || false,
          hire_consideration_date: interview.hire_consideration_date,
          decision_date: interview.decision_date,
          offer_amount: interview.offer_amount,
          rejection_reason: interview.rejection_reason,
          created_at: interview.created_at ? new Date(interview.created_at) : new Date(),
          updated_at: interview.updated_at ? new Date(interview.updated_at) : new Date()
        };
      });

      setInterviews(normalizedInterviews);

      // Calculate statistics
      const newStats = {
        scheduled: normalizedInterviews.filter(i => i.status === 'scheduled').length,
        completed: normalizedInterviews.filter(i => i.status === 'completed').length,
        expired: normalizedInterviews.filter(i => i.status === 'expired').length,
        cancelled: normalizedInterviews.filter(i => i.status === 'cancelled').length,
        total: normalizedInterviews.length
      };
      setStats(newStats);

    } catch (error) {
      console.error('Error loading interviews:', error);
      setError(error.message || 'Failed to load interviews');
      setInterviews([]);
      setStats({ scheduled: 0, completed: 0, expired: 0, cancelled: 0, total: 0 });
    } finally {
      setLoading(false);
    }
  }, [employerId]);

  // Filtered interviews based on current filters
  const filteredInterviews = useMemo(() => {
    let filtered = [...interviews];

    // Apply search filter
    if (memoizedFilters.searchQuery) {
      const searchLower = memoizedFilters.searchQuery.toLowerCase();
      filtered = filtered.filter(interview =>
        interview.candidate_name?.toLowerCase().includes(searchLower) ||
        interview.job_title?.toLowerCase().includes(searchLower) ||
        interview.candidate_email?.toLowerCase().includes(searchLower)
      );
    }

    // Apply date range filter
    if (memoizedFilters.dateRange && memoizedFilters.dateRange !== 'all') {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      
      filtered = filtered.filter(interview => {
        // Handle invalid or missing dates
        if (!interview.scheduled_date || interview.scheduled_date === 'TBD') {
          return false; // Exclude interviews without valid dates
        }
        
        const interviewDate = new Date(interview.scheduled_date);
        
        // Check if the date is valid
        if (isNaN(interviewDate.getTime())) {
          return false; // Exclude interviews with invalid dates
        }
        
        switch (memoizedFilters.dateRange) {
          case 'today':
            return interviewDate.toDateString() === today.toDateString();
          case 'this_week': {
            const weekStart = new Date(today.getTime() - today.getDay() * 24 * 60 * 60 * 1000);
            const weekEnd = new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000);
            return interviewDate >= weekStart && interviewDate <= weekEnd;
          }
          case 'this_month':
            return interviewDate.getMonth() === now.getMonth() && 
                   interviewDate.getFullYear() === now.getFullYear();
          default:
            return true;
        }
      });
    }

    // Apply interview type filter
    if (memoizedFilters.interviewType && memoizedFilters.interviewType !== 'all') {
      filtered = filtered.filter(interview => interview.type === memoizedFilters.interviewType);
    }

    // Apply hiring status filter
    if (memoizedFilters.hiringStatus && memoizedFilters.hiringStatus !== 'all') {
      filtered = filtered.filter(interview => 
        interview.hiring_status === memoizedFilters.hiringStatus
      );
    }

    return filtered;
  }, [interviews, memoizedFilters]);

  // Sorted interviews based on criteria
  const sortedInterviews = useMemo(() => {
    const sorted = [...filteredInterviews];
    
    sorted.sort((a, b) => {
      switch (memoizedFilters.sortBy) {
        case 'date_asc': {
          const dateA = a.scheduled_at ? new Date(a.scheduled_at) : new Date(0);
          const dateB = b.scheduled_at ? new Date(b.scheduled_at) : new Date(0);
          return dateA - dateB;
        }
        case 'date_desc': {
          const dateA = a.scheduled_at ? new Date(a.scheduled_at) : new Date(0);
          const dateB = b.scheduled_at ? new Date(b.scheduled_at) : new Date(0);
          return dateB - dateA;
        }
        case 'name_asc':
          return (a.candidate_name || '').localeCompare(b.candidate_name || '');
        case 'status':
          return (a.status || '').localeCompare(b.status || '');
        case 'hiring_status':
          return (a.hiring_status || 'interviewed').localeCompare(b.hiring_status || 'interviewed');
        default: {
          const dateA = a.scheduled_at ? new Date(a.scheduled_at) : new Date(0);
          const dateB = b.scheduled_at ? new Date(b.scheduled_at) : new Date(0);
          return dateB - dateA;
        }
      }
    });

    return sorted;
  }, [filteredInterviews, memoizedFilters.sortBy]);

  // Interview operations
  const operations = {
    // Update interview status
    updateStatus: useCallback(async (interviewId, newStatus, notes = '') => {
      try {
        const { error } = await supabaseService.interviews.updateInterviewStatus(
          interviewId, 
          newStatus
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { ...interview, status: newStatus, notes }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error updating interview status:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Complete interview with feedback
    completeInterview: useCallback(async (interviewId, feedbackData) => {
      try {
        const { error } = await supabaseService.interviews.completeInterview(
          interviewId, 
          feedbackData
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview, 
                status: 'completed',
                feedback: feedbackData.feedback || '',
                rating: feedbackData.rating || null
              }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error completing interview:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Reschedule interview
    rescheduleInterview: useCallback(async (interviewId, newDateTime, notes = '') => {
      try {
        const { error } = await supabaseService.interviews.rescheduleInterview(
          interviewId, 
          newDateTime, 
          notes
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        const newDate = new Date(newDateTime);
        
        // Validate the new date
        if (isNaN(newDate.getTime())) {
          throw new Error('Invalid date provided for reschedule');
        }
        
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview, 
                scheduled_at: newDate,
                scheduled_date: newDate.toISOString().split('T')[0],
                scheduled_time: newDate.toTimeString().slice(0, 5),
                notes
              }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error rescheduling interview:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Cancel interview
    cancelInterview: useCallback(async (interviewId, reason = '') => {
      try {
        const { error } = await supabaseService.interviews.cancelInterview(
          interviewId, 
          reason
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview, 
                status: 'cancelled',
                notes: reason ? `Cancelled: ${reason}` : 'Interview cancelled'
              }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error cancelling interview:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Update existing interview
    updateInterview: useCallback(async (interviewId, interviewData) => {
      try {
        const { interview, error } = await supabaseService.interviews.updateInterview(
          interviewId,
          interviewData
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview,
                ...interviewData,
                updated_at: new Date().toISOString()
              }
            : interview
        ));
        
        return { success: true, interview };
      } catch (error) {
        console.error('Error updating interview:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Create new interview
    createInterview: useCallback(async (interviewData) => {
      try {
        const { interview, error } = await supabaseService.interviews.createInterview(
          interviewData
        );
        
        if (error) throw new Error(error);
        
        // Refresh interviews to get the latest data
        await fetchInterviews();
        
        return { success: true, interview };
      } catch (error) {
        console.error('Error creating interview:', error);
        return { success: false, error: error.message };
      }
    }, [fetchInterviews]),

    // Update hiring decision
    updateHiringDecision: useCallback(async (interviewId, decisionData) => {
      try {
        const { error } = await supabaseService.interviews.updateHiringStatus(
          interviewId,
          decisionData.status,
          {
            notes: decisionData.notes,
            offer_amount: decisionData.offer_amount,
            rejection_reason: decisionData.rejection_reason
          }
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview, 
                hiring_status: decisionData.status,
                hiring_notes: decisionData.notes,
                decision_date: new Date(),
                offer_amount: decisionData.offer_amount,
                rejection_reason: decisionData.rejection_reason
              }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error updating hiring decision:', error);
        return { success: false, error: error.message };
      }
    }, []),

    // Update hiring status (separate method for hiring decisions)
    updateHiringStatus: useCallback(async (interviewId, hiringStatus, additionalData = {}) => {
      try {
        const { error } = await supabaseService.interviews.updateHiringStatus(
          interviewId,
          hiringStatus,
          additionalData
        );
        
        if (error) throw new Error(error);
        
        // Optimistically update local state
        setInterviews(prev => prev.map(interview => 
          interview.id === interviewId 
            ? { 
                ...interview, 
                hiring_status: hiringStatus,
                hiring_notes: additionalData.notes || interview.hiring_notes,
                decision_date: new Date().toISOString(),
                offer_amount: additionalData.offer_amount || interview.offer_amount,
                rejection_reason: additionalData.rejection_reason || interview.rejection_reason
              }
            : interview
        ));
        
        return { success: true };
      } catch (error) {
        console.error('Error updating hiring status:', error);
        return { success: false, error: error.message };
      }
    }, [])
  };

  // Load interviews on mount and when employerId changes
  useEffect(() => {
    fetchInterviews();
  }, [fetchInterviews]);

  return {
    interviews: sortedInterviews,
    loading,
    error,
    stats,
    operations,
    refresh: fetchInterviews
  };
};

export default useInterviews;